import { Ban, Link2, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/catalyst/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  ExternalTaskRuleSchema,
  useExternalTaskRuleDestroyMutation,
  useExternalTaskRulePartialUpdateMutation,
} from '@/services/api/firmsService';

interface RuleCardProps {
  rule: ExternalTaskRuleSchema;
  onEdit: () => void;
}

export const RuleCard: React.FC<RuleCardProps> = ({ rule, onEdit }) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [updateRule, { isLoading: isUpdating }] =
    useExternalTaskRulePartialUpdateMutation();
  const [deleteRule, { isLoading: isDeleting }] =
    useExternalTaskRuleDestroyMutation();

  const isEnabled = rule.status === 'ENABLED';

  const handleToggleStatus = async () => {
    try {
      await updateRule({
        firmId: rule.firm_id,
        id: rule.id,
        patchedUpdateExternalTaskRuleSchema: {
          status: isEnabled ? 'DISABLED' : 'ENABLED',
        },
      }).unwrap();

      toast.success(`Rule ${isEnabled ? 'disabled' : 'enabled'} successfully`);
    } catch {
      toast.error('Failed to update rule status');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteRule({ firmId: rule.firm_id, id: rule.id }).unwrap();
      toast.success('Rule deleted successfully');
      setShowDeleteDialog(false);
    } catch {
      toast.error('Failed to delete rule');
    }
  };

  const filterCount = countActiveFilters(rule);

  return (
    <>
      <div
        className={cn(
          'group relative overflow-hidden rounded-xl border bg-white transition-all duration-200',
          isEnabled
            ? 'border-slate-200 hover:border-slate-300 hover:shadow-md'
            : 'border-slate-100 opacity-70 hover:opacity-100',
        )}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3">
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-base font-semibold text-slate-900">
              {rule.name}
            </h3>
            {rule.description && (
              <p className="mt-1 line-clamp-2 text-sm text-slate-500">
                {rule.description}
              </p>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <Switch
              checked={isEnabled}
              onChange={handleToggleStatus}
              disabled={isUpdating}
              color="emerald"
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 opacity-0 transition-opacity group-hover:opacity-100"
                >
                  <MoreHorizontal className="h-4 w-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onEdit}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit Rule
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setShowDeleteDialog(true)}
                  className="text-red-600 focus:text-red-600"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Rule
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Badges Row */}
        <div className="flex flex-wrap items-center gap-2 px-4 pb-3">
          {/* Rule Type Badge */}
          <div
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
              rule.rule_type === 'EXCLUSION'
                ? 'bg-amber-50 text-amber-700'
                : 'bg-blue-50 text-blue-700',
            )}
          >
            {rule.rule_type === 'EXCLUSION' ? (
              <Ban className="h-3 w-3" />
            ) : (
              <Link2 className="h-3 w-3" />
            )}
            {rule.rule_type === 'EXCLUSION' ? 'Exclusion' : 'Linking'}
          </div>

          {/* Status Badge */}
          <div
            className={cn(
              'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium',
              isEnabled
                ? 'bg-emerald-50 text-emerald-700'
                : 'bg-slate-100 text-slate-500',
            )}
          >
            {isEnabled ? 'Enabled' : 'Disabled'}
          </div>

          {/* SLA Badge */}
          {rule.match_sla && (
            <div className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
              SLA: {formatDuration(rule.match_sla)}
            </div>
          )}

          {/* Filter Count */}
          {filterCount > 0 && (
            <div className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
              {filterCount} filter{filterCount !== 1 && 's'}
            </div>
          )}

          {/* Version Badge */}
          {rule.active_version && (
            <div className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500">
              v{rule.active_version.version_number}
            </div>
          )}
        </div>

        {/* Edit Button */}
        <div className="border-t border-slate-100 px-4 py-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
            className="w-full justify-center gap-2 text-slate-600 hover:bg-slate-50 hover:text-slate-900"
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit Rule
          </Button>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{rule.name}&rdquo;? This
              action cannot be undone and will remove all versions of this rule.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

/**
 * Count the number of active filters configured for a rule
 */
function countActiveFilters(rule: ExternalTaskRuleSchema): number {
  const config = rule.active_version?.config;
  if (!config) return 0;

  let count = 0;
  const ext = config.external_task_filter;
  const int = config.internal_task_filter;

  if (ext) {
    if (ext.name_contains?.length) count += 1;
    if (ext.name_not_contains?.length) count += 1;
    if (ext.description_contains?.length) count += 1;
    if (ext.statuses?.length) count += 1;
    if (ext.assignee_names?.length) count += 1;
  }

  if (int) {
    if (int.task_group_ids?.length) count += 1;
    if (int.task_type_ids?.length) count += 1;
    if (int.firm_task_template_ids?.length) count += 1;
  }

  return count;
}

/**
 * Parse ISO 8601 duration (e.g., "P0DT2H" = 2 hours) and format for display
 */
function formatDuration(iso: string): string {
  const match = iso.match(/P(?:(\d+)D)?T?(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return iso;

  const [, days, hours, minutes] = match;
  const d = parseInt(days || '0', 10);
  const h = parseInt(hours || '0', 10);
  const m = parseInt(minutes || '0', 10);

  const parts: string[] = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);

  return parts.length > 0 ? parts.join(' ') : '0m';
}
